/**
 * Print utility functions for Follow-up Sheet History
 */

/**
 * Get value for a specific test and date from history reports
 */
export const getHistoryValue = (historyReports, testId, date, isBreakdown = false, breakdownName = '') => {
    const report = historyReports.find(r => {
        const reportDate = new Date(r.date).toISOString().split('T')[0];
        const targetDate = new Date(date).toISOString().split('T')[0];
        return reportDate === targetDate;
    });

    if (!report || !report.testResults) return '';

    // Match test by ID (handle both string and object formats)
    const testResult = report.testResults.find(tr => {
        const trTestId = typeof tr.followupTest === 'object' && tr.followupTest?._id
            ? tr.followupTest._id.toString()
            : tr.followupTest?.toString();
        const targetTestId = typeof testId === 'object' && testId?._id
            ? testId._id.toString()
            : testId?.toString();
        return trTestId === targetTestId;
    });

    if (!testResult) return '';

    if (isBreakdown) {
        const breakdown = testResult.breakdownResults?.find(br => br.name === breakdownName);
        return breakdown?.value || '';
    }

    return testResult.value || '';
};

/**
 * Generate print HTML content
 */
export const generatePrintContent = (patientInfo, historyReports, allTestsMap, getHistoryValueFn) => {
    return `
        <div class="print-header">
            <h2>Investigations Profile</h2>
            <div class="patient-info">
                <div class="patient-info-item">
                    <span class="patient-info-label">Patient Name:</span> ${patientInfo.name}
                </div>
                <div class="patient-info-item">
                    <span class="patient-info-label">Patient ID:</span> ${patientInfo.patientId}
                </div>
                <div class="patient-info-item">
                    <span class="patient-info-label">Age:</span> ${patientInfo.age}
                </div>
                <div class="patient-info-item">
                    <span class="patient-info-label">Gender:</span> ${patientInfo.gender}
                </div>
                <div class="patient-info-item">
                    <span class="patient-info-label">Print Date:</span> ${new Date().toLocaleDateString()}
                </div>
            </div>
        </div>
        
        <table class="print-table">
            <thead>
                <tr>
                    <th class="test-name-col">Test Name</th>
                    ${historyReports.map((report) =>
                        `<th>${new Date(report.date).toLocaleDateString()}</th>`
                    ).join('')}
                </tr>
            </thead>
            <tbody>
                ${Object.values(allTestsMap).map((test) => {
                    let rows = `
                        <tr>
                            <td class="test-name-cell">${test.name}</td>
                            ${historyReports.map((report) => {
                                const value = getHistoryValueFn(test._id, report.date, false);
                                return `<td>${value || '-'}</td>`;
                            }).join('')}
                        </tr>
                    `;

                    if (test.breakdowns && test.breakdowns.length > 0) {
                        test.breakdowns.forEach((breakdown) => {
                            rows += `
                                <tr class="breakdown-row">
                                    <td class="test-name-cell breakdown-name">${breakdown.name}</td>
                                    ${historyReports.map((report) => {
                                        const value = getHistoryValueFn(test._id, report.date, true, breakdown.name);
                                        return `<td>${value || '-'}</td>`;
                                    }).join('')}
                                </tr>
                            `;
                        });
                    }

                    return rows;
                }).join('')}
            </tbody>
        </table>
        
        <div class="print-footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
    `;
};

/**
 * Print styles CSS
 */
export const getPrintStyles = () => {
    return `
        @media print {
            @page {
                size: A4;
                margin: 10mm;
            }
            body * {
                visibility: hidden;
            }
            #print-container, #print-container * {
                visibility: visible;
            }
            #print-container {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
            }
        }
        #print-container {
            font-family: Arial, sans-serif;
            font-size: 11px;
            padding: 20px;
            background: white;
        }
        .print-header {
            margin-bottom: 20px;
            border-bottom: 2px solid black;
            padding-bottom: 15px;
        }
        .print-header h2 {
            margin: 0 0 10px 0;
            color: black;
            font-size: 18px;
        }
        .patient-info {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin-top: 10px;
        }
        .patient-info-item {
            margin: 5px 0;
        }
        .patient-info-label {
            font-weight: bold;
            color: black;
        }
        .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
        }
        .print-table thead {
            background-color: #f5f5f5;
        }
        .print-table th {
            border: 1px solid black;
            padding: 8px;
            text-align: center;
            font-weight: 600;
            font-size: 11px;
            background-color: #f5f5f5;
        }
        .print-table th.test-name-col {
            text-align: left;
            min-width: 200px;
        }
        .print-table td {
            border: 1px solid black;
            padding: 6px 8px;
            text-align: center;
            font-size: 10px;
        }
        .print-table td.test-name-cell {
            text-align: left;
            font-weight: 600;
            color: black;
            background-color: white;
        }
        .print-table td.test-name-cell.breakdown-name {
            padding-left: 30px;
            font-weight: 500;
            color: black;
            font-size: 10px;
        }
        .print-table tr.breakdown-row {
            background-color: #fafafa;
        }
        .print-table tr.breakdown-row td.test-name-cell {
            background-color: #fafafa;
        }
        .print-footer {
            margin-top: 20px;
            text-align: right;
            font-size: 10px;
            color: #666;
        }
    `;
};

/**
 * Handle print functionality
 */
export const handlePrint = (patientInfo, historyReports, allTestsMap, filename) => {
    // Store original title to restore later
    const originalTitle = document.title;

    // Create a hidden div for printing
    const printDiv = document.createElement('div');
    printDiv.id = 'print-container';
    
    // Create getHistoryValue function bound to historyReports
    const getHistoryValueFn = (testId, date, isBreakdown = false, breakdownName = '') => {
        return getHistoryValue(historyReports, testId, date, isBreakdown, breakdownName);
    };
    
    printDiv.innerHTML = generatePrintContent(patientInfo, historyReports, allTestsMap, getHistoryValueFn);

    // Add print styles
    const printStyles = document.createElement('style');
    printStyles.id = 'print-styles';
    printStyles.innerHTML = getPrintStyles();

    // Remove existing print container and styles if any
    const existingPrintDiv = document.getElementById('print-container');
    const existingStyles = document.getElementById('print-styles');
    if (existingPrintDiv) existingPrintDiv.remove();
    if (existingStyles) existingStyles.remove();

    // Append to body
    document.body.appendChild(printStyles);
    document.body.appendChild(printDiv);

    // Set document title for filename (some browsers use this for print/save filename)
    document.title = filename;

    // Trigger print
    setTimeout(() => {
        window.print();

        // Clean up after printing
        setTimeout(() => {
            // Restore original title
            document.title = originalTitle;
            
            if (printDiv.parentNode) {
                printDiv.parentNode.removeChild(printDiv);
            }
            if (printStyles.parentNode) {
                printStyles.parentNode.removeChild(printStyles);
            }
        }, 100);
    }, 100);
};
